import { useLocale } from '@ecomm/data-hooks'
import {
  COOKIE_FIRST_LEAD_SUBMISSION,
  getCookie,
  getPartnerCookie,
  getVisitorId,
  setCookie
} from '@ecomm/shared-cookies'
import { isValidUrl } from '@ecomm/utils/isValidUrl'
import { get as sessionStorageGet } from '@ecomm/utils/sessionStorage'

import { getDeviceType } from '@simplisafe/ss-ecomm-data/utils/windowScreenSize'
import { useCallback, useContext } from 'react'

import * as E from 'fp-ts/lib/Either'
import * as O from 'fp-ts/lib/Option'
import * as TE from 'fp-ts/lib/TaskEither'
import { pipe } from 'fp-ts/lib/function'
import { useAtom } from 'jotai'

import type { ErrorResponse } from '@ecomm/data-simplisafe-api'
import {
  type PromotionsApiResponse,
  fetchCurrentPromotion
} from '@ecomm/data-simplisafe-api'
import type { NinetailedExperience } from '@ecomm/data-storage/ninetailed-experiences'
import { experienceAtom } from '@ecomm/data-storage/ninetailed-experiences'
import { SESSION_STORAGE_FIRST_VISITED_HREF } from '../TrackingProvider/constants'
import { ReferrerUrlContext } from '../TrackingProvider/useReferrerUrl'
import { brazeSetCustomUserAttributes, brazeTrackPageVisit } from '../braze'
import { useGoogleAnalytics } from '../useGoogleAnalytics'
import type { LeadGenV2ResponseProps } from './leadGenCaptureV2'
import {
  type LeadGenCaptureV2Props,
  leadGenCaptureV2
} from './leadGenCaptureV2'

/**
 * In this hook we gather data required by the new eis-leads service POST /v1/leads endpoint
 * as described here https://simplisafe.atlassian.net/wiki/spaces/EIS/pages/4056154277/EIS+Leads+Feature+Brief#POST-%2Fv1%2Fleads
 *
 * The V2 suffix in its name is because this is intended to replace the current ecomm-leads service
 * in the future. See https://simplisafe.atlassian.net/wiki/spaces/EIS/pages/4056154277/EIS+Leads+Feature+Brief#Steps
 *
 * @returns Function that takes LeadGenCaptureV2Props and can be used to call the eis-leads POST /v1/leads endpoint with the provided data.
 */
export function useLeadGenCaptureV2(): (
  data: LeadGenCaptureV2Props,
  subspace?: boolean,
  originKey?: string
) => TE.TaskEither<Error | ErrorResponse, LeadGenV2ResponseProps> {
  const [clientId] = useGoogleAnalytics()
  const { channel } = useContext(ReferrerUrlContext)
  const [experiencesAtom] = useAtom(experienceAtom)

  // We need to keep track of the user's first visited URL within a session
  // If a user navigates to a different page and submits a lead form on that page, we want to capture their channel based on the first page that they visited
  // We can't rely on referrer in this case, as it will be set to the SimpliSafe Site URL once the user navigates to another page
  const firstVisitedHref = sessionStorageGet(SESSION_STORAGE_FIRST_VISITED_HREF)
  const leadFirstUrl = isValidUrl(firstVisitedHref)
    ? new URL(firstVisitedHref)
    : globalThis.window?.location
  const { pathname: urlPath, search } = leadFirstUrl || {}

  const locale = useLocale()

  return useCallback(
    (data: LeadGenCaptureV2Props, subspace = false, originKey = '') => {
      return pipe(
        TE.tryCatch(
          () =>
            fetchCurrentPromotion(
              locale,
              null,
              pipe(
                experiencesAtom,
                O.getOrElse((): readonly NinetailedExperience[] => [])
              )
            ),
          e => (e instanceof Error ? e : new Error(JSON.stringify(e)))
        ),
        TE.chain(promotionResult => {
          const { promoCodeWithMonitoring, promoCode } = pipe(
            promotionResult,
            E.fold(
              () => ({ promoCodeWithMonitoring: '', promoCode: '' }),
              (promo: PromotionsApiResponse) => ({
                promoCodeWithMonitoring: promo.activeCodes.withMonitoring,
                promoCode: promo.activeCodes.withoutMonitoring
              })
            )
          )

          const isFirstSubmission =
            (getCookie(COOKIE_FIRST_LEAD_SUBMISSION) ?? 'true') === 'true'
          const partnerData = getPartnerCookie()
          const vid = getVisitorId()
          const deviceType = getDeviceType().toLowerCase()
          const queryParams = new URLSearchParams(search)
          const utmSource = queryParams.get('utm_source') || ''
          const utmMedium = queryParams.get('utm_medium') || ''
          const utmCampaign = queryParams.get('utm_campaign') || ''
          const country = locale === 'en-US' ? 'US' : 'GB'

          const siteMetadata = {
            rudderstackId: clientId,
            partner: partnerData?.partnerName,
            vid,
            channel,
            urlPath,
            deviceType,
            promoOffer: promoCodeWithMonitoring || promoCode || 'NO_CODE',
            utmSource,
            utmMedium,
            utmCampaign,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
          const firstPromoSeen = siteMetadata.promoOffer
          const lastPromoSeen = data?.siteMetadata?.promoOffer || firstPromoSeen

          return pipe(
            leadGenCaptureV2(
              {
                country,
                ...data,
                siteMetadata: {
                  ...siteMetadata,
                  ...data.siteMetadata
                }
              },
              subspace
            ),
            TE.flatMapEither(result =>
              E.tryCatch(
                () => {
                  brazeTrackPageVisit(originKey)
                  if (isFirstSubmission) {
                    brazeSetCustomUserAttributes({
                      'last promo seen': lastPromoSeen,
                      'first seen promo': firstPromoSeen
                    })
                    setCookie(COOKIE_FIRST_LEAD_SUBMISSION, 'false')
                  } else {
                    brazeSetCustomUserAttributes({
                      'last promo seen': lastPromoSeen
                    })
                  }

                  return result
                },
                e => (e instanceof Error ? e : new Error(JSON.stringify(e)))
              )
            )
          )
        })
      )
    },
    [search, locale, clientId, channel, urlPath, experiencesAtom]
  )
}
